import React from 'react';
import { Link } from 'react-router-dom';

function Feature({ icon, title, description }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        <span className="text-blue-400">{icon}</span>
      </div>
      <div>
        <h3 className="text-base font-semibold text-gray-100 mb-1">{title}</h3>
        <p className="text-sm text-gray-400 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function Step({ number, title, description }) {
  return (
    <div className="flex flex-col items-center text-center gap-3">
      <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
        {number}
      </div>
      <h3 className="text-base font-semibold text-gray-100">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed max-w-xs">{description}</p>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="text-gray-200">
      {/* Nav */}
      <nav className="border-b border-gray-800 bg-gray-950/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" />
              </svg>
            </div>
            <span className="font-bold text-white">MintBridge</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-gray-400 hover:text-gray-100 transition-colors px-3 py-1.5">
              Sign in
            </Link>
            <Link to="/signup" className="btn-primary text-sm py-1.5">
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-20 text-center">
        <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 text-xs text-blue-400 font-medium mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
          Now on Liquid Network Testnet
        </div>
        <h1 className="text-5xl font-extrabold text-white leading-tight mb-6">
          Accept Credit Cards.<br />
          <span className="text-blue-400">Settle in Bitcoin.</span>
        </h1>
        <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
          MintBridge enables high-risk merchants to accept card payments and receive
          Liquid Bitcoin settlement — with zero chargeback risk, powered by Simplicity smart contracts.
        </p>
        <div className="flex items-center justify-center gap-4">
          <Link to="/signup" className="btn-primary text-base px-8 py-3">
            Start Accepting Cards
          </Link>
          <a href="#how-it-works" className="btn-secondary text-base px-8 py-3">
            How It Works
          </a>
        </div>

        {/* Hero stats */}
        <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto">
          {[
            { value: '0%', label: 'Chargeback Risk' },
            { value: 'L-BTC', label: 'Settlement Asset' },
            { value: '$5', label: 'Per Transaction' },
          ].map(stat => (
            <div key={stat.label} className="text-center">
              <div className="text-2xl font-bold text-blue-400">{stat.value}</div>
              <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="border-t border-gray-800 py-20 bg-gray-900/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-white mb-3">How It Works</h2>
            <p className="text-gray-400">Three steps, zero risk</p>
          </div>
          <div className="grid grid-cols-3 gap-12 relative">
            {/* Connector line */}
            <div className="absolute top-6 left-1/4 right-1/4 h-px bg-gray-700 hidden md:block" />
            <Step
              number="1"
              title="Customer Pays with Card"
              description="Your customer checks out using any major credit or debit card via our Stripe-powered checkout."
            />
            <Step
              number="2"
              title="MintBridge Converts"
              description="We instantly convert the payment to Liquid Bitcoin at the best available rate with a $5 flat fee."
            />
            <Step
              number="3"
              title="You Receive L-BTC"
              description="95% settles to your Liquid wallet immediately. 5% is held in escrow for the chargeback window — then released."
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold text-white mb-3">Built for High-Risk Merchants</h2>
          <p className="text-gray-400">Everything you need, nothing you don't</p>
        </div>
        <div className="grid grid-cols-2 gap-10">
          <Feature
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            }
            title="Zero Chargeback Risk"
            description="Simplicity smart contracts on the Liquid Network hold the 5% escrow. Chargebacks are handled automatically — you never lose more than the escrow amount."
          />
          <Feature
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            }
            title="Instant L-BTC Settlement"
            description="Liquid Network transactions settle in about 2 minutes. Receive 95% of every sale in Liquid Bitcoin, directly to your wallet."
          />
          <Feature
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Transparent Pricing"
            description="Flat $3 platform fee plus $2 relay spread per transaction. No hidden fees, no percentage cuts, no monthly minimums."
          />
          <Feature
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            }
            title="Easy Integration"
            description="Drop in our checkout button with two lines of JavaScript. Webhooks notify your backend of payments and chargebacks in real time."
          />
          <Feature
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
            title="High-Risk Friendly"
            description="CBD, adult entertainment, crypto services, firearms, nutraceuticals — industries that traditional processors reject. We serve them all."
          />
          <Feature
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
            title="Real-Time Dashboard"
            description="Monitor payments, escrow balances, and chargebacks in real time. Full transaction history with Liquid block explorer links."
          />
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-gray-800 py-20 bg-gray-900/40">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Simple, Transparent Pricing</h2>
          <p className="text-gray-400 mb-10">One flat fee per transaction. No surprises.</p>

          <div className="card text-left">
            <div className="text-center mb-8">
              <div className="text-4xl font-extrabold text-white mb-1">$5</div>
              <div className="text-gray-400 text-sm">per transaction</div>
            </div>
            <div className="space-y-3 mb-8">
              {[
                { label: 'Platform fee', amount: '$3.00', desc: 'MintBridge processing & infrastructure' },
                { label: 'Relay spread', amount: '$2.00', desc: 'BTC/USD conversion spread' },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between py-2 border-b border-gray-800">
                  <div>
                    <div className="text-sm font-medium text-gray-200">{item.label}</div>
                    <div className="text-xs text-gray-500">{item.desc}</div>
                  </div>
                  <div className="text-sm font-mono text-gray-300">{item.amount}</div>
                </div>
              ))}
            </div>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-sm text-blue-300">
              <strong className="font-semibold">95/5 Settlement Split:</strong> 95% to your L-BTC wallet instantly, 5% held in escrow for chargeback protection (released after 10 Liquid confirmations).
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">
          Ready to accept cards and settle in Bitcoin?
        </h2>
        <p className="text-gray-400 mb-8 text-lg">
          Get started in minutes. No approval process, no monthly fees.
        </p>
        <Link to="/signup" className="btn-primary text-base px-10 py-3 inline-block">
          Create Your Account
        </Link>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" />
              </svg>
            </div>
            <span className="text-sm text-gray-400">MintBridge © 2026</span>
          </div>
          <div className="flex gap-6">
            <Link to="/login" className="text-sm text-gray-500 hover:text-gray-300 transition-colors">Sign In</Link>
            <Link to="/signup" className="text-sm text-gray-500 hover:text-gray-300 transition-colors">Sign Up</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
